<template>
    <div id="survey-list">
        <div id="worksends-page-header" class="tw-mb-3">
            <div class="breadcrumbs" id="breadcrumbs">
                <router-link class="breadcrumb-link" to="/surveys">Bookblock HUB Surveys</router-link>
            </div>
            <h3>Your Bookblock HUB Surveys</h3>
            <div class="ws-button ws-button-main" @click="onShowCreate">Create New Survey</div>
        </div>
        <div v-if="loaded && showJumbotron" class="jumbotron jumbotron-surveys tw-p-5 tw-rounded tw-flex tw-justify-between tw-flex-col" :class="surveys.length?'small':'enlarged'">
            <div class="tw-flex tw-justify-between tw-items-start">
                <h2 class="tw-text-white tw-text-lg">Ask <span v-html="surveys.length?'more':'the'"></span> right questions.</h2>
                <a class="hide tw-text-white tw-text-xs" @click="hideJumbotron">Hide</a>
            </div>
            <div class="desc text-white">
                <p>
                    Gain customer insight by asking just the right questions. Choose to customise one of our industry-optimised
                    <a href="#">survey templates</a> for customer <a href="#">research</a>, customer <a href="#">feedback</a>, lead <a href="#">capture</a>, <a href="#">events</a> and many more applications. 
                    You can also easily build any custom survey from scratch.
                </p>
            </div>
            <div class="jumbotron-buttons">
                <a href="#" class="ws-button ws-button-white" @click.stop="onShowCreate">
                    <span v-if="surveys.length"><b>Create A New Survey</b></span>
                    <span v-else><b>Get Started</b> - Create Your First Survey</span>
                </a>
            </div>
        </div>
        <div class="grid surveys tw-mt-4">
            <div v-for="survey in surveys" :key="survey.id" class="item survey tw-bg-white tw-rounded" @click="goToDetail(survey.id)">
                <div class="tw-rounded tw-flex tw-flex-col">
                    <div class="page-preview">
                        <page-view v-if="survey.pages && survey.pages.length"
                                   :page="startingPage(survey.pages)"
                                   :styles="survey.theme ? survey.theme.styles : {}"
                                   :editable="false"
                                   :readonly="true"
                                   view="preview"
                        />
                    </div>
                    <h3 class="tw-font-normal">{{ survey.name }}</h3>
                    <div class="category">{{ surveyTypeLabel(survey.category) }}</div>
                    <span class="created">Created {{ survey.created_at | moment("DD/MM/YY [at] HH:mm")  }}</span>
                    <div class="stats tw-mt-3 tw-grid tw-grid-cols-2">
                        <div class="stat tw-text-center tw-flex tw-flex-col tw-justify-center">
                            <span class="value">{{ survey.questions_count || 0}}</span>
                            <span class="name">{{ ((survey.questions_count || 0) === 1) ? 'Question' : 'Questions' }}</span>
                        </div>
                        <div class="stat tw-text-center tw-flex tw-flex-col tw-justify-center">
                            <span class="value">{{ survey.complete_responses_count || 0 }}</span>
                            <span class="name">{{ ((survey.complete_responses_count || 0) === 1) ? 'Answer' : 'Answers' }}</span>
                        </div>
                    </div>
                </div>
                <div class="buttons tw-flex tw-justify-between tw-mt-3">
                    <router-link class="ws-button ws-button-main small" :to="{ name: 'surveys-details', params: { survey_id: survey.id }}">
                        View Survey
                    </router-link>
                    <router-link class="ws-button small ws-button-inverted" @click.native="$event.stopImmediatePropagation()" :to="{ name: 'surveys-preview', params: { survey_id: survey.id }}" target="_blank">
                        Preview
                    </router-link>
                </div>
            </div>
        </div>

        <modal v-if="showCreateModal" @close="showCreateModal = false" :width="createFromScratchConfirmed?450:540">
            <h3 v-html="surveys.length ? 'Create New Survey' : 'Create Your First Survey'"></h3>
            <div v-if="!createFromScratchConfirmed" id="new-survey-crossroads" class="tw-grid tw-grid-cols-2">
                <router-link to="surveys/templates" class="new-survey-crossroads-option">
                    <h4>Choose A Template</h4>
                    <p>Pick and customise one of our industry tailored templates.</p>
                    <div class="ws-button ws-button-main">View Template Gallery</div>
                </router-link>
                <div class="new-survey-crossroads-option" @click="createFromScratchConfirmed=true">
                    <h4>Start From Scratch</h4>
                    <p>Create your own custom survey from scratch.</p>
                    <div class="ws-button">Create A New Survey</div>
                </div>
            </div>
            <survey-settings-form v-else @save="onCreateSurvey" @cancel="onCloseCreate"/>
        </modal>
    </div>
</template>

<script>
import Modal from '@/components/modals/Modal';
import SurveySettingsForm from "@/pages/surveys/SurveySettingsForm";
import PageView from "@/pages/surveys/PageView.vue";

export default {
    name: 'SurveyIndex',
    components: {PageView, SurveySettingsForm, Modal },
    data() {
        return {
            loaded: false,
            jumbotron: true,
            showCreateModal: false,
            image: '',
            createFromScratchConfirmed: false,
        }
    },
    computed: {
        showJumbotron() {
            return this.$cookies.get('surveys_jumbotron_hidden') ? false : this.jumbotron;
        },
        surveys() {
            return this.$store.getters["surveys/orderedList"]
        },
        surveyTypes() {
            return this.$store.state.surveyTypes;
        }
    },
    created() {
        window.vm = this;
        this.onLoadSurveys()
    },
    methods: {
        startingPage(pages) {
            if (pages.filter(p => p.order === 1).length) return pages.filter(p => p.order === 1)[0];
            else return pages[0];
        },
        onShowCreate() {
            this.showCreateModal = true;
        },
        onCloseCreate() {
            this.showCreateModal = false;
            this.createFromScratchConfirmed = false;
        },
        goToDetail(id) {
            this.$router.push({ name: 'surveys-details', params: { survey_id: id } });
        },
        hideJumbotron() {
            this.jumbotron = false;
            this.$cookies.set('surveys_jumbotron_hidden', true);
        },
        surveyTypeLabel(slug) {
            return this.surveyTypes.filter(type => type.value === slug)[0].text;
        },
        async onCreateSurvey(data) {
            try {
                const {id} = await this.$store.dispatch('surveys/create', data);
                return this.$router.push({name: 'surveys-details-questions', params: { survey_id: id }})
            } catch ({response}) {
                if(response) {
                    this.notifyStatus(response.status, 'Validation Failed', response.data.message, response);
                }
            }
        },
        async onLoadSurveys() {
            await this.$store.dispatch('surveys/getList', {'get-pages': true, 'get-theme': true})
            this.loaded = true;
        }
    },
}
</script>
